import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api";
import { matchesProductLabel, getProductLabels, formatProductName } from "../product-utils";
import type { PullRequestInfo, ProductsResponse } from "../types";
import "../components/pr-detail-dialog";

import "@bee/pattern-library";

@customElement("promotions-page")
export class PromotionsPage extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: Geist, sans-serif;
    }

    .controls {
      display: flex;
      align-items: center;
      gap: var(--spacing-12, 12px);
      margin-bottom: var(--spacing-16, 16px);
    }

    .pr-count {
      font-size: 13px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .pr-list {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-12, 12px);
    }

    bee-card {
      --padding: var(--spacing-16, 16px);
    }

    bee-card::part(body) {
      padding: var(--spacing-16, 16px);
    }

    bee-card::part(base) {
      transition: box-shadow 0.15s ease;
    }

    bee-card:hover::part(base) {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .pr-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--spacing-8, 8px);
    }

    .pr-number {
      font-size: 12px;
      font-weight: 600;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      white-space: nowrap;
    }

    .pr-title {
      font-size: 14px;
      font-weight: 500;
      margin-top: var(--spacing-8, 8px);
    }

    .pr-meta {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
      font-size: 12px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      margin-top: var(--spacing-8, 8px);
    }

    .change-list {
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-4, 4px) var(--spacing-8, 8px);
      margin-top: var(--spacing-8, 8px);
    }

    .change-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 8px;
      background: var(--colors-background-bg-gray-50, #f5f6f8);
      border-radius: var(--radius-sm, 6px);
      font-size: 12px;
      font-family: monospace;
    }

    .change-name {
      font-weight: 500;
      color: var(--colors-text-brand-900, #002266);
    }

    .change-arrow {
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .change-from {
      color: var(--colors-foreground-fg-danger-500, #c62828);
      text-decoration: line-through;
    }

    .change-to {
      color: var(--colors-foreground-fg-success-500, #2e7d32);
    }

    .empty {
      padding: var(--spacing-24, 24px);
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      text-align: center;
      font-size: 13px;
    }
  `;

  @property({ attribute: false }) activeProductFilter: string | null = null;
  @property({ attribute: false }) products: ProductsResponse | null = null;

  @state() private _prs: PullRequestInfo[] = [];
  @state() private _loading = true;
  @state() private _error = "";
  @state() private _activePr: PullRequestInfo | null = null;

  async connectedCallback() {
    super.connectedCallback();
    await this._refresh();
  }

  private async _refresh() {
    this._loading = true;
    this._error = "";
    try {
      const res = await api.getPullRequests();
      this._prs = res.pullRequests;
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : "Failed to load PRs";
    } finally {
      this._loading = false;
    }
  }

  private _formatDate(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  private _isRelease(pr: PullRequestInfo): boolean {
    return pr.target === "prod";
  }

  private _openDetail(pr: PullRequestInfo) {
    this._activePr = pr;
  }

  private _closeDetail() {
    this._activePr = null;
  }

  render() {
    if (this._loading) {
      return html`<div class="empty"><bee-spinner size="medium" type="blue"></bee-spinner></div>`;
    }
    if (this._error) {
      return html`<bee-alert variant="error" open><span slot="message">${this._error}</span></bee-alert>`;
    }
    const filtered = this._prs.filter((pr) =>
      matchesProductLabel(pr.labels, this.activeProductFilter)
    );
    return html`
      <div class="controls">
        <bee-button label="Refresh" bee-type="secondary-grey" size="small" icon="refresh" @click=${() => this._refresh()}></bee-button>
        ${this._prs.length > 0 ? html`<span class="pr-count">${filtered.length} open PR${filtered.length !== 1 ? "s" : ""}</span>` : nothing}
      </div>
      <div class="pr-list">
        ${this._prs.length === 0
          ? html`<div class="empty">No open promotion PRs</div>`
          : filtered.map((pr) => this._renderPr(pr))}
      </div>
      ${this._activePr
        ? html`<pr-detail-dialog
            .pr=${this._activePr}
            @close=${() => this._closeDetail()}
            @patched=${() => this._refresh()}
          ></pr-detail-dialog>`
        : nothing}
    `;
  }

  private _renderPr(pr: PullRequestInfo) {
    const isRelease = this._isRelease(pr);
    const isHotfix = pr.labels?.includes("hotfix") ?? false;
    const typeLabel = isRelease ? "Release" : "Promotion";
    const typeColor = isRelease ? "warning" : "brand";
    const productLabels = getProductLabels(pr.labels);

    return html`
      <bee-card @click=${() => this._openDetail(pr)} style="cursor: pointer;">
        <div class="pr-header">
          <span class="pr-number">
            #${pr.number}
            <bee-tag color=${typeColor} small>${typeLabel}</bee-tag>
            ${isHotfix ? html`<bee-tag color="error" small>Hotfix</bee-tag>` : nothing}
            ${productLabels.map((p) => html`<bee-tag color="gray" small>${formatProductName(p)}</bee-tag>`)}
            <bee-tag type="badge" color="gray" small noborder>${pr.author}</bee-tag>
          </span>
          <span class="pr-number">${this._formatDate(pr.createdAt)}</span>
        </div>
        <div class="pr-title">
          <bee-link href=${pr.url} target="_blank" rel="noopener noreferrer" @click=${(e: Event) => e.stopPropagation()}>${pr.title}</bee-link>
        </div>
        ${pr.changes.length > 0 ? html`
          <div class="change-list">
            ${pr.changes.map((c) => html`
              <span class="change-chip">
                <span class="change-name">${c.name}</span>
                <span class="change-from">${c.from}</span>
                <span class="change-arrow">-></span>
                <span class="change-to">${c.to}</span>
              </span>
            `)}
          </div>
        ` : nothing}
      </bee-card>
    `;
  }
}
